import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';

export default function AgentPod() {
  return (
    <>
      <Helmet>
        <title>AgentPod – Run AI assistants safely, without surprises | RailTech</title>
        <meta
          name="description"
          content="AgentPod runs your AI work in a safe pod so nothing leaks, costs stay predictable, and you get a clear paper trail. Start a pilot to see it in action."
        />
        <meta
          name="keywords"
          content="AgentPod, AI safety, AI execution, AI monitoring, audit logs, private AI, RailTech"
        />
        <meta name="author" content="RailTech Team" />
        <meta property="og:title" content="AgentPod – Run AI assistants safely, without surprises" />
        <meta
          property="og:description"
          content="Ship AI features with guardrails, clear logs, and predictable costs—on your cloud or on-prem."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://railtech.io/agentpod" />
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <div className="relative isolate overflow-hidden bg-gradient-to-b from-indigo-100/20">
          <div className="mx-auto max-w-7xl px-6 pb-24 pt-10 sm:pb-32 lg:flex lg:px-8 lg:py-40">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl lg:flex-shrink-0 lg:pt-8"
            >
              <h1 className="mt-10 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
                AgentPod - Run AI assistants safely, without surprises
              </h1>
              <p className="mt-6 text-lg leading-8 text-gray-600">
                AgentPod puts every AI run in a safe “pod.” That means no data leaking,
                no runaway prompts, and no mystery bills, ust clean results and a clear record
                of what happened.
              </p>
              <div className="mt-10 flex items-center gap-x-6">
                <a
                  href="https://docs.google.com/forms/d/e/1FAIpQLSdbONmDE6-aaqr62oksbJqj7H32u0CjEV0vpZ3L5fAcUXomQA/viewform?usp=dialog"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-md bg-[#6C63FF] px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-[#5A52E0] transition-colors"
                >
                  Join Pilot
                </a>
               
              </div>
              <p className="mt-4 text-sm text-gray-500">
                Deploy on your cloud, on-prem, or offline. No lock-in.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Benefits (plain language) */}
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mx-auto max-w-2xl lg:text-center"
          >
            <h2 className="text-base font-semibold leading-7 text-[#6C63FF]">Why teams try AgentPod</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Ship AI with guardrails, proof, and predictable cost
            </p>
          </motion.div>

          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {[
                {
                  title: 'Safe by default',
                  description:
                    'Each run happens in its own sandbox. If something goes wrong, it stays contained.'
                },
                {
                  title: 'Stop bad prompts early',
                  description:
                    'We check inputs for risky tricks before they reach your model. Block or fix them automatically.'
                },
                {
                  title: 'Only the right context',
                  description:
                    'We attach just the memory the model needs. Better answers, lower tokens, no data sprawl.'
                },
                {
                  title: 'Clear receipts',
                  description:
                    'See what ran, when, and why. Useful for debugging, audits, and customer trust.'
                },
                {
                  title: 'Yours to control',
                  description:
                    'Run in your VPC or on-prem. Keep data where you want it—even fully offline.'
                },
                {
                  title: 'Fast start, real scale',
                  description:
                    'Add one service, get value in days. Grow to many agents when you’re ready.'
                }
              ].map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                  className="flex flex-col"
                >
                  <dt className="text-base font-semibold leading-7 text-gray-900">{feature.title}</dt>
                  <dd className="mt-1 flex flex-auto flex-col text-base leading-7 text-gray-600">
                    <p className="flex-auto">{feature.description}</p>
                  </dd>
                </motion.div>
              ))}
            </dl>
          </div>
        </div>

       
        {/* Who it's for */}
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mx-auto max-w-3xl"
          >
            <h3 className="text-2xl font-bold tracking-tight text-gray-900">Who it’s for</h3>
            <ul className="mt-6 list-disc pl-6 text-gray-700 space-y-3">
              <li><span className="font-semibold">Product teams</span> shipping AI features but blocked by risk or compliance.</li>
              <li><span className="font-semibold">Ops & support</span> who need reliable assistants that don’t “go off script.”</li>
              <li><span className="font-semibold">Security & IT</span> who want control, logs, and a clean way to say “yes.”</li>
            </ul>
          </motion.div>
        </div>

        {/* Simple FAQ (no jargon) */}
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mx-auto max-w-3xl"
          >
            <h3 className="text-2xl font-bold tracking-tight text-gray-900">FAQ, simply answered</h3>
            <div className="mt-6 space-y-6 text-gray-700">
              <div>
                <p className="font-semibold">What’s a “pod”?</p>
                <p>Think of a pod like a private workbench for each run. It’s clean, contained, and torn down after use.</p>
              </div>
              <div>
                <p className="font-semibold">Will this lock us in?</p>
                <p>No. You host it. Your cloud, your data. Swap models and tools as you like.</p>
              </div>
              <div>
                <p className="font-semibold">Does it slow things down?</p>
                <p>No. Most teams see fewer retries and faster resolution because problems are caught early.</p>
              </div>
              <div>
                <p className="font-semibold">What about cost?</p>
                <p>Using only the right context means fewer tokens and clearer bills. You’ll see where time and money go.</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* CTA */}
        <div className="bg-white">
          <div className="mx-auto max-w-7xl py-24 sm:px-6 sm:py-32 lg:px-8">
            <div className="relative isolate overflow-hidden bg-gray-900 px-6 py-24 text-center shadow-2xl sm:rounded-3xl sm:px-16">
              <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Try it with your own use case
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-gray-300">
                Join the pilot. Bring one assistant. We’ll help you make it safe, fast, and auditable.
              </p>
              <div className="mt-10 flex items-center justify-center gap-x-6">
                <a
                  href="https://docs.google.com/forms/d/e/1FAIpQLSdbONmDE6-aaqr62oksbJqj7H32u0CjEV0vpZ3L5fAcUXomQA/viewform?usp=dialog"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                >
                  Sign Up
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
